import React, { useState } from "react";
import { Box, Typography, Grid, Card, CardContent, Divider, Paper } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import createStyles from "@mui/styles/createStyles";
import { Alert } from "@mui/material";
import { PrimaryButton, SecondaryButton } from "../../../../layout/Button/Button";
import { useBranchedStoryEdit } from "../../contexts/BranchedStoryEditContext";
import { VCard } from "../../../../../core/components/VCard";

const useStyles = makeStyles(() =>
    createStyles({
        formContainer: {
            padding: "24px",
        },
        summaryCard: {
            marginBottom: "16px",
            backgroundColor: "#f9f9f9",
        },
        changeItem: {
            marginBottom: "8px",
        },
        originalValue: {
            color: "#d32f2f",
            textDecoration: "line-through",
            fontStyle: "italic",
        },
        newValue: {
            color: "#1FAF8C",
            fontWeight: "bold",
        },
        actionButtons: {
            marginTop: "32px",
            display: "flex",
            gap: "16px",
            justifyContent: "flex-end",
        },
        statsGrid: {
            marginTop: "16px",
        },
        statItem: {
            textAlign: "center",
            padding: "16px",
        },
        statNumber: {
            fontSize: "2rem",
            fontWeight: "bold",
            color: "#1FAF8C",
        },
        statLabel: {
            color: "#666",
            fontSize: "0.875rem",
        },
    }),
);

interface ReviewChangesProps {
    onComplete: () => void;
}

export const ReviewChanges: React.FC<ReviewChangesProps> = ({ onComplete }) => {
    const classes = useStyles();
    const {
        originalStory,
        currentStory,
        hasChanges,
        hasPartChanges,
        hasDecisionChanges,
        hasQuestionChanges,
        editedParts,
        editedDecisionPoints,
        editedQuestions,
        saveChanges,
        loading,
        error,
    } = useBranchedStoryEdit();
    const [saving, setSaving] = useState(false);
    const [saveSuccess, setSaveSuccess] = useState(false);

    const handleSaveAndComplete = async () => {
        setSaving(true);
        setSaveSuccess(false);
        try {
            await saveChanges();
            setSaveSuccess(true);
            // Wait a moment to show success message before completing
            setTimeout(() => {
                onComplete();
            }, 1500);
        } catch (err) {
            // Error is handled by the context
            console.error("Failed to save changes:", err);
        } finally {
            setSaving(false);
        }
    };

    // Detect changes between original and current story
    const changes = [];
    if (originalStory.storyName !== currentStory.storyName) {
        changes.push({
            field: "Story Name",
            original: originalStory.storyName,
            current: currentStory.storyName,
        });
    }
    if (originalStory.author !== currentStory.author) {
        changes.push({
            field: "Author",
            original: originalStory.author,
            current: currentStory.author,
        });
    }
    if (originalStory.synopsis !== currentStory.synopsis) {
        changes.push({
            field: "Synopsis",
            original: originalStory.synopsis || "(empty)",
            current: currentStory.synopsis || "(empty)",
        });
    }
    if (originalStory.ageRange !== currentStory.ageRange) {
        changes.push({
            field: "Age Range",
            original: originalStory.ageRange || "(empty)",
            current: currentStory.ageRange || "(empty)",
        });
    }
    if (originalStory.colour !== currentStory.colour) {
        changes.push({
            field: "Card Color",
            original: originalStory.colour || "(none)",
            current: currentStory.colour || "(none)",
        });
    }
    if (originalStory.resources !== currentStory.resources) {
        changes.push({
            field: "Resources Link (NI)",
            original: originalStory.resources || "(empty)",
            current: currentStory.resources || "(empty)",
        });
    }
    if (originalStory.resourcesROI !== currentStory.resourcesROI) {
        changes.push({
            field: "Resources Link (ROI)",
            original: originalStory.resourcesROI || "(empty)",
            current: currentStory.resourcesROI || "(empty)",
        });
    }

    // Add story parts changes
    if (hasPartChanges) {
        editedParts.forEach((partUpdate, partId) => {
            const originalPart = currentStory.StoryParts?.find((p) => p.id === partId);
            if (originalPart) {
                if (partUpdate.content !== undefined && partUpdate.content !== originalPart.content) {
                    changes.push({
                        field: `Story Part ${originalPart.part} Content`,
                        original: originalPart.content || "(empty)",
                        current: partUpdate.content || "(empty)",
                    });
                }
                if (partUpdate.partLabel !== undefined && partUpdate.partLabel !== originalPart.partLabel) {
                    changes.push({
                        field: `Story Part ${originalPart.part} Label`,
                        original: originalPart.partLabel || "(empty)",
                        current: partUpdate.partLabel || "(empty)",
                    });
                }
            }
        });
    }

    // Add decision points changes
    if (hasDecisionChanges) {
        editedDecisionPoints.forEach((dpUpdate, dpId) => {
            if (dpUpdate.decisionText !== undefined) {
                changes.push({
                    field: `Decision Point ${dpId} Text`,
                    original: "(original)",
                    current: dpUpdate.decisionText,
                });
            }
        });
    }

    // Add questions changes
    if (hasQuestionChanges) {
        editedQuestions.forEach((questionUpdate, questionId) => {
            if (questionUpdate.question !== undefined) {
                changes.push({
                    field: `Question ${questionId}`,
                    original: "(original)",
                    current: questionUpdate.question,
                });
            }
            if (questionUpdate.keywords !== undefined) {
                changes.push({
                    field: `Question ${questionId} Keywords`,
                    original: "(original)",
                    current: questionUpdate.keywords,
                });
            }
        });
    }

    // Story statistics
    const partCount = currentStory.StoryParts?.length || 0;
    const decisionPointCount =
        currentStory.StoryParts?.reduce((count, part) => count + (part.StoryDecisionPoints?.length || 0), 0) || 0;
    const questionCount =
        currentStory.StoryParts?.reduce((count, part) => count + (part.MappedStoryQuestion?.length || 0), 0) || 0;

    return (
        <VCard>
            <Box className={classes.formContainer}>
                <Typography variant="h4" gutterBottom>
                    Review & Save Changes
                </Typography>

                {/* Success notification */}
                {saveSuccess && (
                    <Alert severity="success" style={{ marginBottom: 16 }}>
                        <strong>Success!</strong> Your branched story changes have been saved successfully. All edits
                        are now persisted in the database.
                    </Alert>
                )}

                {/* Error notification */}
                {error && (
                    <Alert severity="error" style={{ marginBottom: 16 }}>
                        <strong>Save Failed:</strong> {error}. Please try again or contact support if the problem
                        persists.
                    </Alert>
                )}

                {/* Changes Summary */}
                {hasChanges ? (
                    <Card className={classes.summaryCard}>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>
                                Changes Summary ({changes.length} modifications)
                            </Typography>
                            {changes.map((change, index) => (
                                <Box key={index} className={classes.changeItem}>
                                    <Typography variant="subtitle2">{change.field}:</Typography>
                                    <Typography variant="body2" className={classes.originalValue}>
                                        {change.original}
                                    </Typography>
                                    <Typography variant="body2" className={classes.newValue}>
                                        → {change.current}
                                    </Typography>
                                    {index < changes.length - 1 && <Divider style={{ margin: "8px 0" }} />}
                                </Box>
                            ))}
                        </CardContent>
                    </Card>
                ) : (
                    <Paper
                        style={{
                            padding: "16px",
                            marginBottom: "16px",
                            backgroundColor: "#e3f2fd",
                            border: "1px solid #64b5f6",
                        }}
                    >
                        <Typography color="primary" variant="body2">
                            <strong>Info:</strong> No changes detected. All fields match the original story.
                        </Typography>
                    </Paper>
                )}

                {/* Story Statistics */}
                <Card style={{ marginBottom: "16px" }}>
                    <CardContent>
                        <Typography variant="h6" gutterBottom>
                            Story Structure Overview
                        </Typography>
                        <Grid container spacing={2} className={classes.statsGrid}>
                            <Grid item xs={3}>
                                <Box className={classes.statItem}>
                                    <Typography className={classes.statNumber}>{partCount}</Typography>
                                    <Typography className={classes.statLabel}>Story Parts</Typography>
                                </Box>
                            </Grid>
                            <Grid item xs={3}>
                                <Box className={classes.statItem}>
                                    <Typography className={classes.statNumber}>{decisionPointCount}</Typography>
                                    <Typography className={classes.statLabel}>Decision Points</Typography>
                                </Box>
                            </Grid>
                            <Grid item xs={3}>
                                <Box className={classes.statItem}>
                                    <Typography className={classes.statNumber}>{questionCount}</Typography>
                                    <Typography className={classes.statLabel}>Questions</Typography>
                                </Box>
                            </Grid>
                            <Grid item xs={3}>
                                <Box className={classes.statItem}>
                                    <Typography className={classes.statNumber}>
                                        {currentStory.Themes?.length || 0}
                                    </Typography>
                                    <Typography className={classes.statLabel}>Themes</Typography>
                                </Box>
                            </Grid>
                        </Grid>
                    </CardContent>
                </Card>

                {/* Changes Summary by Category */}
                {(hasPartChanges || hasDecisionChanges || hasQuestionChanges) && (
                    <Card style={{ marginBottom: "16px" }}>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>
                                Content Changes Overview
                            </Typography>
                            <Grid container spacing={2}>
                                {hasPartChanges && (
                                    <Grid item xs={4}>
                                        <Box className={classes.statItem}>
                                            <Typography className={classes.statNumber}>{editedParts.size}</Typography>
                                            <Typography className={classes.statLabel}>Story Parts Modified</Typography>
                                        </Box>
                                    </Grid>
                                )}
                                {hasDecisionChanges && (
                                    <Grid item xs={4}>
                                        <Box className={classes.statItem}>
                                            <Typography className={classes.statNumber}>
                                                {editedDecisionPoints.size}
                                            </Typography>
                                            <Typography className={classes.statLabel}>
                                                Decision Points Modified
                                            </Typography>
                                        </Box>
                                    </Grid>
                                )}
                                {hasQuestionChanges && (
                                    <Grid item xs={4}>
                                        <Box className={classes.statItem}>
                                            <Typography className={classes.statNumber}>
                                                {editedQuestions.size}
                                            </Typography>
                                            <Typography className={classes.statLabel}>Questions Modified</Typography>
                                        </Box>
                                    </Grid>
                                )}
                            </Grid>
                        </CardContent>
                    </Card>
                )}

                {/* Action Buttons */}
                <Box className={classes.actionButtons}>
                    <SecondaryButton type="button" disabled={saving || loading}>
                        Cancel
                    </SecondaryButton>
                    <PrimaryButton onClick={handleSaveAndComplete} disabled={saving || loading}>
                        {saving ? "Saving..." : hasChanges ? "Save Changes & Complete" : "Complete"}
                    </PrimaryButton>
                </Box>
            </Box>
        </VCard>
    );
};

// Display name for debugging (following design guide)
ReviewChanges.displayName = "ReviewChanges";
